<script lang="ts" module>
  import { Accordion, type AccordionProps } from 'melt/builders';
  import type { Snippet } from 'svelte';

  import BaseCard from '@/ui/elements/card/BaseCard.svelte';
  import CardHeader from '@/ui/elements/card/CardHeader.svelte';
  import CardLoading from '@/ui/elements/card/CardLoading.svelte';
  import { type ExpandableCardItem } from '@/ui/elements/card/ExpandableCard.svelte';
  import FooterMoreActions, {
    type FooterMoreActionsProps,
  } from '@/ui/elements/tpi-drawer/footerMoreActions/FooterMoreActions.svelte';
  import type { ReactiveValue } from '@/utils/reactive-value/reactive-value';

  import AssistantCard, { type AssistantCardProps } from './AssistantCard/AssistantCard.svelte';
  import NoErrors from './AssistantCard/NoErrors.svelte';
  import VersionScore from './AssistantCard/VersionScore.svelte';

  export type CardProps = { key: string; aiLoading?: ReactiveValue<boolean> } & Omit<
    AssistantCardProps,
    'onImprovementClick'
  >;

  type AdditionalCardItem = {
    card: CardProps;
  } & ExpandableCardItem;

  export interface AssistantProps {
    title: string;
    cards: CardProps[];
    onImprovementClick?: (improvementKey: string) => void;
    infoTextLabel?: string;
    loading?: boolean;
    dimension?: string;
    onDimensionChange?: (dimension: string | undefined) => void;
    noErrorsInfo?: Snippet;
    noErrorsContent?: {
      title: string;
      text: string;
    };
    helpTarget?: FooterMoreActionsProps['helpTarget'];
    onLoadAiSuggestionClick?: () => Promise<void>;
  }
</script>

<script lang="ts">
  let { dimension: dimensionProp, ...props }: AssistantProps = $props();

  let assistantState: 'loading' | 'results' | 'noImprovements' = $derived(
    props.loading ? 'loading'
    : props.cards.length > 0 ? 'results'
    : 'noImprovements',
  );

  let items: AdditionalCardItem[] = $derived.by(() => {
    let expandItems: AdditionalCardItem[] = [];
    let id = 0;

    for (let card of props.cards) {
      expandItems.push({
        id: 'id-' + id,
        key: card.key,
        disabled: false,
        card: card,
      });

      id++;
    }

    return expandItems;
  });

  function getItemById(id: string | undefined): AdditionalCardItem | undefined {
    return items.find((item) => item.id === id);
  }

  let accProps: AccordionProps<boolean> = {
    onValueChange: (...args) => {
      dimensionProp = getItemById(args[0] as string)?.key;
      props.onDimensionChange?.(dimensionProp);
      return true;
    },
  };

  function getItemByKey(key: string | undefined): AdditionalCardItem | undefined {
    return items.find((item) => item.key === key);
  }

  const accordion = new Accordion(accProps);

  let lastDimensionProp: string | undefined;

  $effect(() => {
    if (dimensionProp === lastDimensionProp) return;

    const key = dimensionProp || lastDimensionProp;
    const item = getItemByKey(key);

    if (item) {
      const action = dimensionProp ? 'expand' : 'collapse';
      accordion.getItem(item)[action]();
    }

    lastDimensionProp = dimensionProp;
  });
</script>

<BaseCard class="relative">
  <CardHeader cardLabel={props.title}>
    <VersionScore score={props.cards.length} loading={assistantState === 'loading'}></VersionScore>
  </CardHeader>
</BaseCard>

<div
  class="mt-[-10px] grid w-full gap-2 overflow-hidden rounded-b-md bg-prim px-3 pb-3 pt-6 shadow-card"
  {...accordion.root}
>
  {#if assistantState === 'loading'}
    {#each Array(5) as _, i (i)}
      <div class="flex h-12">
        <CardLoading></CardLoading>
      </div>
    {/each}
  {:else if assistantState === 'results'}
    {#each items as item (item.id)}
      <AssistantCard
        open={accordion.getItem(item).isExpanded}
        useAccordion={{ accordion, item }}
        {...item.card}
        onImprovementClick={props.onImprovementClick}
        onLoadAiSuggestionClick={props.onLoadAiSuggestionClick}
      ></AssistantCard>
    {/each}
  {:else if assistantState === 'noImprovements'}
    <div class="grid gap-3">
      {#if props.noErrorsContent}
        <NoErrors
          noErrorText={props.noErrorsContent.text}
          noErrorTitle={props.noErrorsContent.title}
        ></NoErrors>
      {:else}
        <NoErrors noErrorText="Alles erledigt." noErrorTitle="Sehr gut!"></NoErrors>
      {/if}
      {#if props.noErrorsInfo}
        {@render props.noErrorsInfo()}
      {/if}
    </div>
  {/if}
  {#if props.infoTextLabel || props.helpTarget}
    <FooterMoreActions label="Info" helpTarget={props.helpTarget} infoText={props.infoTextLabel}
    ></FooterMoreActions>
  {/if}
</div>
